import { useState } from "react";
import api, { API_BASE, formatApiError } from "@/lib/api";
import { useCompany } from "@/contexts/CompanyContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { FileUp, Download, AlertCircle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function ImportEmployeesPage() {
  const { companies, companyParam } = useCompany();
  const [company, setCompany] = useState(companyParam || companies[0]?.id || "");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [committing, setCommitting] = useState(false);

  const downloadTemplate = async () => {
    try {
      const resp = await api.get("/employees/import/template", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const link = document.createElement("a");
      link.href = url; link.download = "modelo_colaboradores.xlsx"; link.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Falha ao baixar modelo"); }
  };

  const upload = async () => {
    if (!file || !company) { toast.error("Selecione arquivo e empresa"); return; }
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const token = localStorage.getItem("rh_token");
      const resp = await fetch(`${API_BASE}/employees/import/preview?company_id=${company}`, {
        method: "POST", body: fd,
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(formatApiError(err?.detail) || "Falha ao processar");
      }
      const data = await resp.json();
      setPreview(data);
    } catch (ex) { toast.error(ex.message); }
    finally { setLoading(false); }
  };

  const commit = async () => {
    if (!preview) return;
    setCommitting(true);
    try {
      const { data } = await api.post("/employees/import/commit", { company_id: company, itens: preview.itens });
      toast.success(`${data.inserted} colaborador(es) importados`);
      setPreview(null); setFile(null);
    } catch (ex) { toast.error(formatApiError(ex.response?.data?.detail)); }
    finally { setCommitting(false); }
  };

  return (
    <div className="space-y-6" data-testid="import-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] text-[#8A6B2B] font-medium">Importação em lote</div>
          <h1 className="font-display text-3xl font-semibold">Importar colaboradores · Excel/CSV</h1>
          <p className="mt-1 text-[13.5px] text-muted-foreground">
            Mapeie a planilha, valide matrículas e duplicidades, visualize a prévia antes de comitar.
          </p>
        </div>
        <Button variant="outline" onClick={downloadTemplate} data-testid="import-template-btn">
          <Download className="h-4 w-4 mr-2" /> Baixar modelo
        </Button>
      </div>

      <div className="rounded-lg border border-border bg-white p-5 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium block mb-1">Empresa destino</label>
            <Select value={company} onValueChange={setCompany}>
              <SelectTrigger data-testid="import-company"><SelectValue placeholder="Selecione" /></SelectTrigger>
              <SelectContent>
                {companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.nome_fantasia}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium block mb-1">Arquivo (.xlsx / .csv)</label>
            <input
              type="file"
              accept=".xlsx,.csv"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              data-testid="import-file-input"
              className="block w-full text-sm text-foreground file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:bg-[#234935] file:text-white file:text-xs file:font-medium hover:file:bg-[#2c5c43] cursor-pointer"
            />
          </div>
          <div className="flex items-end">
            <Button onClick={upload} disabled={!file || !company || loading}
              className="bg-[#234935] hover:bg-[#2c5c43] w-full" data-testid="import-upload-btn">
              <FileUp className="h-4 w-4 mr-2" /> {loading ? "Analisando..." : "Analisar prévia"}
            </Button>
          </div>
        </div>
        <div className="mt-4 rounded-md border border-[#E8D9AE] bg-[#F9F4E7] px-3 py-2 text-[12px] text-[#5C4320] flex items-start gap-2" data-testid="import-lgpd-notice">
          <AlertCircle className="h-3.5 w-3.5 mt-0.5 shrink-0 text-[#8A6B2B]" />
          <span>
            <b>LGPD:</b> documentos pessoais (CPF/RG/PIS/CTPS) <b>não são mais aceitos</b>.
            A <b>matrícula</b> é o identificador único e é lida como texto, preservando zeros à esquerda.
          </span>
        </div>
      </div>

      {preview && (
        <div className="rounded-lg border border-border bg-white overflow-hidden">
          <div className="flex items-center justify-between px-5 py-3 border-b border-border flex-wrap gap-2">
            <div className="flex items-center gap-3 text-[13px]">
              <span className="font-display text-[15px] font-semibold">Prévia de importação</span>
              <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">
                <CheckCircle2 className="h-3 w-3 mr-1" /> {preview.ok} válidos
              </Badge>
              <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">
                <AlertCircle className="h-3 w-3 mr-1" /> {preview.erros} com erro
              </Badge>
            </div>
            <Button onClick={commit} disabled={committing || preview.ok === 0}
              className="bg-[#234935] hover:bg-[#2c5c43]" data-testid="import-commit-btn">
              {committing ? "Importando..." : `Importar ${preview.ok} colaborador(es)`}
            </Button>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Linha</TableHead>
                <TableHead>Matrícula</TableHead>
                <TableHead>Nome</TableHead>
                <TableHead>Setor</TableHead>
                <TableHead>Cargo</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {preview.itens.map((r) => (
                <TableRow key={r.linha}>
                  <TableCell className="font-mono text-[11px]">{r.linha}</TableCell>
                  <TableCell>{r.dados.matricula}</TableCell>
                  <TableCell>{r.dados.nome}</TableCell>
                  <TableCell>{r.dados.setor_codigo || r.dados.setor}</TableCell>
                  <TableCell>{r.dados.cargo}</TableCell>
                  <TableCell>
                    {r.valido ? (
                      <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">OK</Badge>
                    ) : (
                      <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200" title={r.erros.join("; ")}>
                        {r.erros[0]}
                      </Badge>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
